import { useEffect, useRef } from "react";

export function useQueueSSE(sessionId, token, handlers = {}) {
  const handlersRef = useRef(handlers);
  const esRef = useRef(null);
  const reconnectRef = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    if (!sessionId || !token) return;

    activeRef.current = true;

    const connect = () => {
      if (!activeRef.current) return;

      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }

      const es = new EventSource(
        `/api/queue/${sessionId}/stream?token=${token}`,
      );
      esRef.current = es;

      const dispatch = (handlerName, e) => {
        try {
          const data = JSON.parse(e.data);
          handlersRef.current[handlerName]?.(data);
        } catch {
          /* ignore */
        }
      };

      es.addEventListener("queue_updated", (e) =>
        dispatch("onQueueUpdated", e),
      );
      es.addEventListener("student_called", (e) =>
        dispatch("onStudentCalled", e),
      );
      es.addEventListener("session_paused", (e) => dispatch("onPaused", e));
      es.addEventListener("session_unpaused", (e) => dispatch("onPaused", e));
      es.addEventListener("session_closed", (e) =>
        dispatch("onSessionClosed", e),
      );
      es.addEventListener("policy_changed", (e) =>
        dispatch("onPolicyChanged", e),
      );
      es.addEventListener("announcement", (e) => dispatch("onAnnouncement", e));
      es.addEventListener("direct_message", (e) =>
        dispatch("onDirectMessage", e),
      ); // NEW
      es.addEventListener("swap_requested", (e) =>
        dispatch("onSwapRequested", e),
      );
      es.addEventListener("swap_resolved", (e) =>
        dispatch("onSwapResolved", e),
      );
      es.addEventListener("ping", () => {});

      es.onopen = () => {
        if (reconnectRef.current) {
          clearTimeout(reconnectRef.current);
          reconnectRef.current = null;
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (activeRef.current) {
          reconnectRef.current = setTimeout(connect, 1000);
        }
      };
    };

    connect();

    const pollInterval = setInterval(() => {
      if (activeRef.current && handlersRef.current._onPoll) {
        handlersRef.current._onPoll();
      }
    }, 5000);

    return () => {
      activeRef.current = false;
      clearTimeout(reconnectRef.current);
      clearInterval(pollInterval);
      esRef.current?.close();
      esRef.current = null;
    };
  }, [sessionId, token]);
}
